import {DetailScreen} from '../mapping-classes/detail-screen';
import {DetailLabel} from '../mapping-classes/detail-label';
import {DetailPanel} from '../mapping-classes/detail-panel';
import {LazyLoadingData} from '../mapping-classes/lazy-loading-data';
import {CommissionDataComponent} from './shared/commission-data.component';

export class Composedaccount implements DetailScreen {
  buttons;
  restrictedData: boolean;
  title: string;
  actualityDateTime: string;
  panels: DetailPanel[];
  nameValues: DetailLabel[];
  shieldedText = 'Afgeschermd';
  errorText: string;
  transactionsDetailsBankCode: string;

  constructor(response: any) {
    this.title = 'Samenstelling';
    this.buttons = [];
    if (response.detailsViewObject) {
      this.actualityDateTime = response.detailsViewObject.actualityDateTime;
    }

    if (response.listOfError && response.listOfError.length !== 0) {
      this.createErrorMessage(response);
    } else {
      this.restrictedData = response.detailsViewObject['restrictedData'];
      this.panels = [];
      this.nameValues = [];
      this.setPanels();
      this.setNameAndValues();
      this.addValues(response);
    }
  }

  createErrorMessage(response) {
    if (response.listOfError && response.listOfError.length !== 0) {
      if (response.listOfError[0].code === 'CIV_F025') {
        this.errorText = 'Geen detail gegevens gevonden.';
      } else if (response.listOfError[0].code) {
        this.errorText = 'Product niet gevonden.';
      }
    }
  }

  private nullSafe<T>(someObject: T, defaultValue: T = {} as T): T {
    if (typeof someObject === 'undefined' || someObject === null) {
      return defaultValue;
    }
    return someObject;
  }

  setPanels() {
    this.panels.push(new DetailPanel('Productkenmerken'));
    this.panels.push(new DetailPanel('Productafspraken'));
    this.panels.push(new DetailPanel('Deelnemende rekeningen'));
    this.panels.push(new DetailPanel('Rente/Provisie/Kosten Algemeen'));

  }

  setNameAndValues() {
    const productDetailsPanel: DetailPanel = this.panels[0];
    this.fillProductDetailsPanel(productDetailsPanel);

    const productAgreementsPanel: DetailPanel = this.panels[1];
    this.fillProductAgreementsPanel(productAgreementsPanel);

    const interestProvisionCostGeneralPanel: DetailPanel = this.panels[3];
    this.fillInterestProvisionCostGeneralPanel(interestProvisionCostGeneralPanel);
  }

  addValues(response) {
    const obj = response.detailsViewObject;
    const productDetailsPanel: DetailPanel = this.panels[0];
    this.addValuesToProductDetailsPanel(productDetailsPanel, obj);

    const productAgreementsPanel: DetailPanel = this.panels[1];
    this.addValuesToProductAgreementsPanel(productAgreementsPanel, obj);

    const participatingAccountsPanel: DetailPanel = this.panels[2];
    this.addValuesToParticipatingAccountsPanel(participatingAccountsPanel, obj);

    const interestProvisionCostGeneralPanel: DetailPanel = this.panels[3];
    this.addValuesToInterestProvisionCostGeneralPanel(interestProvisionCostGeneralPanel, obj);

    if (obj.accountAgreements && obj.accountAgreements.length !== 0) {
      let count = 4;
      const sortedAccountAgreements = this.sortForeignCurrencyAccountAgreements(obj.accountAgreements);

      sortedAccountAgreements.forEach((accountAgreement) => {
        if (accountAgreement.currencyCode && accountAgreement.currencyCode === 'EUR') {
          this.panels.push(new DetailPanel('EUR | Rente/Provisie/Kosten'));
          const interestProvisionCostEuroPanel: DetailPanel = this.panels[4];

          this.fillInterestProvisionCostEuroPanel(interestProvisionCostEuroPanel);
          this.addValuesToInterestProvisionCostEuroPanel(interestProvisionCostEuroPanel, accountAgreement);
          this.addSubHeadersToInterestProvisionCostEuroPanel(interestProvisionCostEuroPanel, accountAgreement);
          count = 5;
        }
      });

      sortedAccountAgreements.forEach((accountAgreement) => {
        if (accountAgreement.currencyCode !== 'EUR') {
          this.panels.push(new DetailPanel(' | Rente/Provisie/Kosten'));
          const interestProvisionCostForeignCurrencyPanel: DetailPanel = this.panels[count];

          interestProvisionCostForeignCurrencyPanel.name = accountAgreement.currencyCode + interestProvisionCostForeignCurrencyPanel.name;
          this.fillInterestProvisionCostForeignCurrencyPanel(interestProvisionCostForeignCurrencyPanel);
          this.addValuesToInterestProvisionCostForeignCurrencyPanel(interestProvisionCostForeignCurrencyPanel, accountAgreement);
          this.addSubHeadersToInterestProvisionCostForeignCurrencyPanel(interestProvisionCostForeignCurrencyPanel, accountAgreement);
          count++;
        }
      });
    }
  }

  private sortForeignCurrencyAccountAgreements(accountAgreements: any[]): any[] {
    return accountAgreements.sort((a, b) => a.currencyCode.localeCompare(b.currencyCode, 'nl'));
  }

  private fillProductDetailsPanel(productDetailsPanel: DetailPanel) {
    productDetailsPanel.nameAndValues.set('composedAccountNumber', new DetailLabel('Samenstellingnummer'));
  }

  private addValuesToProductDetailsPanel(productDetailsPanel: DetailPanel, obj) {
    productDetailsPanel.nameAndValues.get('composedAccountNumber').setValue(obj.accountNumber);
  }

  private fillProductAgreementsPanel(productAgreementsPanel: DetailPanel) {
    productAgreementsPanel.nameAndValues.set('startDate', new DetailLabel('Startdatum'));
    productAgreementsPanel.nameAndValues.set('compositionForAmount', new DetailLabel('Samenstelling voor saldo'));
  }

  private addValuesToProductAgreementsPanel(productAgreementsPanel: DetailPanel, obj) {
    productAgreementsPanel.nameAndValues.get('startDate').setValue(obj.startDate);

    if (obj.includesBalance) {
      productAgreementsPanel.nameAndValues.get('compositionForAmount').setValue('Ja');
    } else {
      productAgreementsPanel.nameAndValues.get('compositionForAmount').setValue('Nee');
    }
  }

  private addValuesToParticipatingAccountsPanel(participatingAccountsPanel: DetailPanel, obj) {
    participatingAccountsPanel.hasTable = true;
    participatingAccountsPanel.lazyLoadingData = new LazyLoadingData(null,
      null,
      'participatingaccounts');
    if (obj.participatingAccounts && obj.participatingAccounts.length !== 0) {
      obj.participatingAccounts.forEach((participatingAccount) => {
        participatingAccount.restrictedData = obj.restrictedData;
      });
      participatingAccountsPanel.lazyLoadingData.data = this.sortParticipatingAccounts(obj.participatingAccounts);
    }
  }

  private sortParticipatingAccounts(participatingAccounts: any[]): any[] {
    return participatingAccounts.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber, 'nl'));
  }

  private fillInterestProvisionCostGeneralPanel(interestProvisionCostGeneralPanel: DetailPanel) {
    interestProvisionCostGeneralPanel.nameAndValues.set('bookingRpk',
      new DetailLabel('Boeking RPK'));
    interestProvisionCostGeneralPanel.nameAndValues.set('settlementPeriodInterestProvision',
      new DetailLabel('Afrekenperiode rente/provisie'));
    interestProvisionCostGeneralPanel.nameAndValues.set('settlementPeriodCosts',
      new DetailLabel('Afrekenperiode kosten'));
  }

  private addValuesToInterestProvisionCostGeneralPanel(interestProvisionCostGeneralPanel: DetailPanel, obj) {
    interestProvisionCostGeneralPanel.nameAndValues.get('bookingRpk')
      .setValue(obj.bookingRpk);
    interestProvisionCostGeneralPanel.nameAndValues.get('settlementPeriodInterestProvision')
      .setValue(obj.settlementPeriodInterestProvision);
    interestProvisionCostGeneralPanel.nameAndValues.get('settlementPeriodCosts')
      .setValue(obj.settlementPeriodCosts);
  }

  private fillInterestProvisionCostEuroPanel(interestProvisionCostEuroPanel: DetailPanel) {
    interestProvisionCostEuroPanel.nameAndValues.set('accountNumber',
      new DetailLabel('Afreken rekeningnummer'));
    interestProvisionCostEuroPanel.nameAndValues.set('startDate',
      new DetailLabel('Startdatum'));
    interestProvisionCostEuroPanel.nameAndValues.set('composedAccountType',
      new DetailLabel('Soort samenstelling'));
  }

  private addValuesToInterestProvisionCostEuroPanel(interestProvisionCostEuroPanel: DetailPanel, obj) {
    interestProvisionCostEuroPanel.nameAndValues.get('accountNumber').setValue(obj.accountNumber);
    interestProvisionCostEuroPanel.nameAndValues.get('startDate').setValue(obj.startDate);
    interestProvisionCostEuroPanel.nameAndValues.get('composedAccountType').setValue(obj.composedAccountType);
  }

  private fillInterestProvisionCostForeignCurrencyPanel(interestProvisionCostForeignCurrencyPanel: DetailPanel) {
    interestProvisionCostForeignCurrencyPanel.nameAndValues.set('accountNumber',
      new DetailLabel('Afreken rekeningnummer'));
    interestProvisionCostForeignCurrencyPanel.nameAndValues.set('startDate',
      new DetailLabel('Startdatum'));
    interestProvisionCostForeignCurrencyPanel.nameAndValues.set('composedAccountType',
      new DetailLabel('Soort samenstelling'));
  }

  private addValuesToInterestProvisionCostForeignCurrencyPanel(interestProvisionCostForeignCurrencyPanel: DetailPanel, obj) {
    interestProvisionCostForeignCurrencyPanel.nameAndValues.get('accountNumber')
      .setValue(obj.accountNumber);
    interestProvisionCostForeignCurrencyPanel.nameAndValues.get('startDate')
      .setValue(obj.startDate);
    interestProvisionCostForeignCurrencyPanel.nameAndValues.get('composedAccountType')
      .setValue(obj.composedAccountType);
  }

  private addSubHeadersToInterestProvisionCostEuroPanel(interestCommissionCostEuroPanel: DetailPanel, accountAgreement) {

    const composedPanel = new DetailPanel('Samenstelling voor:');
    this.addFieldsToComposedPanel(composedPanel);
    this.fillComposedPanel(composedPanel, accountAgreement);
    interestCommissionCostEuroPanel.subPanels.push(composedPanel);

    if (accountAgreement.debitDetails) {
      const debitPanel = new DetailPanel('Debet');
      this.addFieldsToDebitPanel(debitPanel);
      this.fillDebitPanel(debitPanel,
        accountAgreement);
      debitPanel.transactionsDetailsBankCode = accountAgreement.debitDetails.bankCode || '';
      interestCommissionCostEuroPanel.subPanels.push(debitPanel);
    }

    if (accountAgreement.creditDetails) {
      const creditPanel = new DetailPanel('Credit');
      this.addFieldsToCreditPanel(creditPanel);
      this.fillCreditPanel(creditPanel,
        accountAgreement);
      creditPanel.transactionsDetailsBankCode = accountAgreement.creditDetails.bankCode || '';
      interestCommissionCostEuroPanel.subPanels.push(creditPanel);
    }

    if (accountAgreement.creditCoreDetails) {
      const creditCorePanel = new DetailPanel('Credit kern');
      this.addFieldsToCreditCorePanel(creditCorePanel);
      this.fillCreditCorePanel(creditCorePanel,
        accountAgreement);
      creditCorePanel.transactionsDetailsBankCode = accountAgreement.creditCoreDetails.bankCode || '';
      interestCommissionCostEuroPanel.subPanels.push(creditCorePanel);
    }

    if (accountAgreement.creditTransactionDetails) {
      const creditTransactionPanel = new DetailPanel('Credit transactiekas');
      this.addFieldsToCreditTransactionPanel(creditTransactionPanel);
      this.fillCreditTransactionPanel(creditTransactionPanel,
        accountAgreement);
      creditTransactionPanel.transactionsDetailsBankCode = accountAgreement.creditTransactionDetails.bankCode || '';
      interestCommissionCostEuroPanel.subPanels.push(creditTransactionPanel);
    }

    if (accountAgreement.commissionDetails) {
      const commissionPanel = new DetailPanel('Provisie');
      this.addFieldsToCommissionPanel(commissionPanel);
      this.fillCommissionPanel(commissionPanel,
        accountAgreement);
      commissionPanel.transactionsDetailsBankCode = accountAgreement.commissionDetails.bankCode || '';
      interestCommissionCostEuroPanel.subPanels.push(commissionPanel);
    }

    if (accountAgreement.costsDetails) {
      const costsPanel = new DetailPanel('Kosten');
      this.addFieldsToCostsPanel(costsPanel);
      this.fillCostsPanel(costsPanel,
        accountAgreement);
      costsPanel.transactionsDetailsBankCode = accountAgreement.costsDetails.bankCode || '';
      interestCommissionCostEuroPanel.subPanels.push(costsPanel);
    }
  }

  private addSubHeadersToInterestProvisionCostForeignCurrencyPanel(
    interestCommissionCostForeignCurrencyPanel: DetailPanel, accountAgreement) {
    const composedPanel = new DetailPanel('Samenstelling voor:');
    this.addFieldsToComposedPanel(composedPanel);
    this.fillComposedPanel(composedPanel,
      accountAgreement);
    interestCommissionCostForeignCurrencyPanel.subPanels.push(composedPanel);

    if (accountAgreement.debitDetails) {
      const debitPanel = new DetailPanel('Debet');
      this.addFieldsToDebitPanel(debitPanel);
      this.fillDebitPanel(debitPanel,
        accountAgreement);
      debitPanel.transactionsDetailsBankCode = accountAgreement.debitDetails.bankCode || '';
      interestCommissionCostForeignCurrencyPanel.subPanels.push(debitPanel);
    }

    if (accountAgreement.creditDetails) {
      const creditPanel = new DetailPanel('Credit');
      this.addFieldsToCreditPanel(creditPanel);
      this.fillCreditPanel(creditPanel,
        accountAgreement);
      creditPanel.transactionsDetailsBankCode = accountAgreement.creditDetails.bankCode || '';
      interestCommissionCostForeignCurrencyPanel.subPanels.push(creditPanel);
    }

    if (accountAgreement.creditCoreDetails) {
      const creditCorePanel = new DetailPanel('Credit kern');
      this.addFieldsToCreditCorePanel(creditCorePanel);
      this.fillCreditCorePanel(creditCorePanel,
        accountAgreement);
      creditCorePanel.transactionsDetailsBankCode = accountAgreement.creditCoreDetails.bankCode || '';
      interestCommissionCostForeignCurrencyPanel.subPanels.push(creditCorePanel);
    }

    if (accountAgreement.creditTransactionDetails) {
      const creditTransactionPanel = new DetailPanel('Credit transactiekas');
      this.addFieldsToCreditTransactionPanel(creditTransactionPanel);
      this.fillCreditTransactionPanel(creditTransactionPanel,
        accountAgreement);
      creditTransactionPanel.transactionsDetailsBankCode = accountAgreement.creditTransactionDetails.bankCode || '';
      interestCommissionCostForeignCurrencyPanel.subPanels.push(creditTransactionPanel);
    }

    if (accountAgreement.commissionDetails) {
      const commissionPanel = new DetailPanel('Provisie');
      this.addFieldsToCommissionPanel(commissionPanel);
      this.fillCommissionPanel(commissionPanel,
        accountAgreement);
      commissionPanel.transactionsDetailsBankCode = accountAgreement.commissionDetails.bankCode || '';
      interestCommissionCostForeignCurrencyPanel.subPanels.push(commissionPanel);
    }

    if (accountAgreement.costsDetails) {
      const costsPanel = new DetailPanel('Kosten');
      this.addFieldsToCostsPanel(costsPanel);
      this.fillCostsPanel(costsPanel,
        accountAgreement);
      costsPanel.transactionsDetailsBankCode = accountAgreement.costsDetails.bankCode || '';
      interestCommissionCostForeignCurrencyPanel.subPanels.push(costsPanel);
    }
  }

  private addFieldsToComposedPanel(composedPanel: DetailPanel) {
    composedPanel.nameAndValues.set('interest',
      new DetailLabel('Rente'));
    composedPanel.nameAndValues.set('commission',
      new DetailLabel('Provisie'));
    composedPanel.nameAndValues.set('cost',
      new DetailLabel('Kosten'));
  }

  private fillComposedPanel(composedPanel: DetailPanel, accountAgreement) {
    if (accountAgreement.includesInterest) {
      composedPanel.nameAndValues.get('interest').setValue('Ja');
    } else {
      composedPanel.nameAndValues.get('interest').setValue('Nee');
    }

    if (accountAgreement.includesCommission) {
      composedPanel.nameAndValues.get('commission').setValue('Ja');
    } else {
      composedPanel.nameAndValues.get('commission').setValue('Nee');
    }

    if (accountAgreement.includesCosts) {
      composedPanel.nameAndValues.get('cost').setValue('Ja');
    } else {
      composedPanel.nameAndValues.get('cost').setValue('Nee');
    }
  }

  private addFieldsToDebitPanel(debitPanel: DetailPanel) {
    debitPanel.nameAndValues.set('debitInterestAgreement',
      new DetailLabel('Debetrente afspraak'));
    debitPanel.nameAndValues.set('agreementStartDate',
      new DetailLabel('Begindatum afspraak'));
    debitPanel.nameAndValues.set('calculationMethod',
      new DetailLabel('Renterekenmethode'));
  }

  private fillDebitPanel(debitPanel: DetailPanel, accountAgreement) {
    debitPanel.nameAndValues.get('debitInterestAgreement')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.debitDetails.tableNumber), null));
    debitPanel.nameAndValues.get('agreementStartDate')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.debitDetails.agreementStartDate), null));
    debitPanel.nameAndValues.get('calculationMethod')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.debitDetails.calculationMethod), null));
  }

  private addFieldsToCreditPanel(creditPanel: DetailPanel) {
    creditPanel.nameAndValues.set('creditInterestAgreement',
      new DetailLabel('Creditrente afspraak'));
    creditPanel.nameAndValues.set('agreementStartDate',
      new DetailLabel('Begindatum afspraak'));
    creditPanel.nameAndValues.set('calculationMethod',
      new DetailLabel('Renterekenmethode'));
    creditPanel.nameAndValues.set('balanceBasis',
      new DetailLabel('Saldo grondslag'));
    creditPanel.nameAndValues.set('mechanism',
      new DetailLabel('Mechanisme'));
  }

  private fillCreditPanel(creditPanel: DetailPanel, accountAgreement) {
    const transactionsDetailsBankCode = accountAgreement.creditTransactionDetails.bankCode || 0;
    creditPanel.nameAndValues.get('creditInterestAgreement')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditDetails.tableNumber), null));
    creditPanel.nameAndValues.get('agreementStartDate')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditDetails.agreementStartDate), null));
    creditPanel.nameAndValues.get('calculationMethod')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditDetails.calculationMethod), null));
    creditPanel.nameAndValues.get('balanceBasis')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditDetails.balanceBasis), null));
    creditPanel.nameAndValues.get('mechanism')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditDetails.mechanism), null));
  }

  private addFieldsToCreditCorePanel(creditCorePanel: DetailPanel) {
    creditCorePanel.nameAndValues.set('creditInterestCoreAgreement',
      new DetailLabel('Creditrente kern afspraak'));
    creditCorePanel.nameAndValues.set('agreementStartDate',
      new DetailLabel('Begindatum afspraak'));
    creditCorePanel.nameAndValues.set('calculationMethod',
      new DetailLabel('Renterekenmethode'));
    creditCorePanel.nameAndValues.set('balanceBasis',
      new DetailLabel('Saldo grondslag'));
    creditCorePanel.nameAndValues.set('mechanism',
      new DetailLabel('Mechanisme'));
  }

  private fillCreditCorePanel(creditCorePanel: DetailPanel, accountAgreement) {
    creditCorePanel.nameAndValues.get('creditInterestCoreAgreement')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditCoreDetails.tableNumber), null));
    creditCorePanel.nameAndValues.get('agreementStartDate')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditCoreDetails.agreementStartDate), null));
    creditCorePanel.nameAndValues.get('calculationMethod')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditCoreDetails.calculationMethod), null));
    creditCorePanel.nameAndValues.get('balanceBasis')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditCoreDetails.balanceBasis), null));
    creditCorePanel.nameAndValues.get('mechanism')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditCoreDetails.mechanism), null));
  }

  private addFieldsToCreditTransactionPanel(creditCorePanel: DetailPanel) {
    creditCorePanel.nameAndValues.set('creditInterestTransactionAgreement',
      new DetailLabel('Creditrente transactiekas afspraak'));
    creditCorePanel.nameAndValues.set('agreementStartDate',
      new DetailLabel('Begindatum afspraak'));
    creditCorePanel.nameAndValues.set('calculationMethod',
      new DetailLabel('Renterekenmethode'));
    creditCorePanel.nameAndValues.set('balanceBasis',
      new DetailLabel('Percentage grondslag'));
    creditCorePanel.nameAndValues.set('mechanism',
      new DetailLabel('Mechanisme'));
  }

  private fillCreditTransactionPanel(creditCorePanel: DetailPanel, accountAgreement) {
    creditCorePanel.nameAndValues.get('creditInterestTransactionAgreement')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditTransactionDetails.tableNumber), null));
    creditCorePanel.nameAndValues.get('agreementStartDate')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditTransactionDetails.agreementStartDate), null));
    creditCorePanel.nameAndValues.get('calculationMethod')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditTransactionDetails.calculationMethod), null));
    creditCorePanel.nameAndValues.get('balanceBasis')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditTransactionDetails.balanceBasis), null));
    creditCorePanel.nameAndValues.get('mechanism')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.creditTransactionDetails.mechanism), null));
  }

  private addFieldsToCommissionPanel(commissionPanel: DetailPanel) {
    commissionPanel.nameAndValues.set('commissionAgreement',
      new DetailLabel('Provisie afspraak'));
    commissionPanel.nameAndValues.set('agreementStartDate',
      new DetailLabel('Begindatum afspraak'));
    commissionPanel.nameAndValues.set('calculationMethod',
      new DetailLabel('Provisierekenmethode'));
  }

  private fillCommissionPanel(commissionPanel: DetailPanel, accountAgreement) {
    commissionPanel.nameAndValues.get('commissionAgreement')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.commissionDetails.tableNumber), null));
    commissionPanel.nameAndValues.get('agreementStartDate')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.commissionDetails.agreementStartDate), null));
    commissionPanel.nameAndValues.get('calculationMethod')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.commissionDetails.calculationMethod), null));
    this.fillCommissionData(accountAgreement.commissionDetails);
  }

  private addFieldsToCostsPanel(costsPanel: DetailPanel) {
    costsPanel.nameAndValues.set('costsAgreement',
      new DetailLabel('Kosten afspraak'));
    costsPanel.nameAndValues.set('agreementStartDate',
      new DetailLabel('Begindatum afspraak'));
  }

  private fillCostsPanel(costsPanel: DetailPanel, accountAgreement) {
    costsPanel.nameAndValues.get('costsAgreement')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.costsDetails.tableNumber), null));
    costsPanel.nameAndValues.get('agreementStartDate')
      .setValue(this.nullSafe(this.nullSafe(accountAgreement.costsDetails.agreementStartDate), null));
  }

  private fillCommissionData(commissionDetails) {
    CommissionDataComponent.minimumRate = commissionDetails.minimumRate;
    CommissionDataComponent.maximumRate = commissionDetails.maximumRate;
    CommissionDataComponent.flatRate = commissionDetails.flatRate;
  }
}
